import { ApiResponse, ValidApiResponseData, ErrorCode, TokenPairResponse } from '@mygate/common';
import { useAuthStore } from '@/stores/authStore';

export class ApiError extends Error {
  code?: keyof typeof ErrorCode;
  status?: number;
  details?: unknown;

  constructor(message: string, code?: keyof typeof ErrorCode, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

interface QueuedRequest<T = unknown> {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
  endpoint: string;
  options?: RequestInit;
}

class ApiClient {
  private baseUrl: string;
  private isRefreshing = false;
  private requestQueue: Array<QueuedRequest<ApiResponse<ValidApiResponseData>>> = [];

  constructor() {
    // In development, we use Vite proxy, so no need for full URL
    this.baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL || '';
  }

  private async handleResponse<T extends ValidApiResponseData>(response: Response): Promise<ApiResponse<T>> {
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        throw new ApiError(`Request failed with status ${response.status}`, undefined, response.status);
      }

      if (errorData?.error) {
        throw new ApiError(
          errorData.error.message || 'Request failed',
          errorData.error.code,
          response.status,
          errorData.error,
        );
      }

      throw new ApiError(`Request failed with status ${response.status}`, undefined, response.status);
    }

    const data = (await response.json()) as ApiResponse<T>;

    if (!data.success) {
      throw new ApiError(data.error?.message || 'Request failed', data.error?.code, response.status, data.error);
    }

    return data;
  }

  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = useAuthStore.getState().refreshToken;

    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/staff/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = (await response.json()) as ApiResponse<TokenPairResponse>;

      if (data.success && data.data) {
        // Update tokens in store
        useAuthStore.getState().setTokens(data.data.accessToken, data.data.refreshToken);
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  private async processQueue(): Promise<void> {
    const queue = [...this.requestQueue];
    this.requestQueue = [];

    for (const request of queue) {
      try {
        const result = await this.request(request.endpoint, request.options);
        request.resolve(result);
      } catch (error) {
        request.reject(error);
      }
    }
  }

  async request<T extends ValidApiResponseData>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get current access token from store
    const accessToken = useAuthStore.getState().accessToken;

    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    };

    const finalOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options?.headers || {}),
      },
    };

    try {
      const response = await fetch(url, finalOptions);

      // Check if we got a 401 and should try to refresh
      if (response.status === 401 && !endpoint.includes('/auth/')) {
        // If we're already refreshing, queue this request
        if (this.isRefreshing) {
          return new Promise<ApiResponse<T>>((resolve, reject) => {
            this.requestQueue.push({
              resolve: resolve as (value: ApiResponse<ValidApiResponseData>) => void,
              reject,
              endpoint,
              options,
            });
          });
        }

        this.isRefreshing = true;

        // Try to refresh the token
        const refreshed = await this.refreshAccessToken();

        if (refreshed) {
          this.isRefreshing = false;

          // Process queued requests
          this.processQueue();

          // Retry the original request with new token
          const newAccessToken = useAuthStore.getState().accessToken;
          finalOptions.headers = {
            ...finalOptions.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };

          const retryResponse = await fetch(url, finalOptions);
          return await this.handleResponse<T>(retryResponse);
        } else {
          this.isRefreshing = false;

          // Clear auth state and redirect to login
          useAuthStore.getState().logout();

          // Process queue with errors
          this.requestQueue.forEach(req => {
            req.reject(new ApiError('Session expired', ErrorCode.UNAUTHORIZED as keyof typeof ErrorCode, 401));
          });
          this.requestQueue = [];

          // Redirect to login
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }

          throw new ApiError('Session expired', ErrorCode.UNAUTHORIZED as keyof typeof ErrorCode, 401);
        }
      }

      return await this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'Network error',
        ErrorCode.INTERNAL_ERROR as keyof typeof ErrorCode,
      );
    }
  }

  async get<T extends ValidApiResponseData>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T extends ValidApiResponseData>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T extends ValidApiResponseData>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T extends ValidApiResponseData>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T extends ValidApiResponseData>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async fetchBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const accessToken = useAuthStore.getState().accessToken;

    const options: RequestInit = {
      method: 'GET',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    };

    const response = await fetch(url, options);

    // Handle 401 with token refresh (same logic as request())
    if (response.status === 401 && !endpoint.includes('/auth/')) {
      if (this.isRefreshing) {
        // Queue and wait for refresh to complete, then retry
        return new Promise<Blob>((resolve, reject) => {
          const checkRefresh = setInterval(() => {
            if (!this.isRefreshing) {
              clearInterval(checkRefresh);
              this.fetchBlob(endpoint).then(resolve).catch(reject);
            }
          }, 100);
        });
      }

      this.isRefreshing = true;
      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        this.isRefreshing = false;
        this.processQueue();

        // Retry with new token
        const newAccessToken = useAuthStore.getState().accessToken;
        options.headers = { Authorization: `Bearer ${newAccessToken}` };
        const retryResponse = await fetch(url, options);

        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({}));
          throw new ApiError(
            errorData.error?.message || 'Failed to download file',
            errorData.error?.code,
            retryResponse.status,
          );
        }
        return retryResponse.blob();
      } else {
        this.isRefreshing = false;
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        throw new ApiError('Session expired', ErrorCode.UNAUTHORIZED as keyof typeof ErrorCode, 401);
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(errorData.error?.message || 'Failed to download file', errorData.error?.code, response.status);
    }

    return response.blob();
  }
}

export const api = new ApiClient();
